// api
import type { PortfolioAnalysisResult } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';

type NullableNumber = number | null;

const HOURS_PER_YEAR = 8760;
const THIRTY_DAY_DATA_THRESHOLD = 0.8;
const MINIMUM_SCORE_CAPS = {
  electricity: {
    outsideKW: 29.04,
    trendImpactKW: 1.824,
  },
} as const;

export type ResourceName = 'electricity' | 'heat' | 'water';
export type ElectricityComponentKey =
  'outsideKW' | 'outsideRatio' | 'trend' | 'intensity';
export type HeatComponentKey =
  'heatingSeason' | 'outsideHeatingSeason' | 'last30' | 'trend12' | 'trend30';
export type WaterComponentKey = 'outside30' | 'trend12' | 'trend30';

export interface ScoreComponentDetails {
  rawValue: NullableNumber;
  cap: NullableNumber;
  score: number;
  weight: number;
  contribution: number;
  available: boolean;
}

export interface ElectricityFactorDetails {
  factor: number;
  averageKW: NullableNumber;
  previousAverageKW: NullableNumber;
  currentYearConsumption: NullableNumber;
  lastYearConsumption: NullableNumber;
  yoyPct: NullableNumber;
  outsideKW: NullableNumber;
  outsideRatio: NullableNumber;
  trendImpactKW: NullableNumber;
  intensityKwhPerM2: NullableNumber;
  outsideKWScore: number;
  outsideRatioScore: number;
  trendScore: number;
  intensityScore: NullableNumber;
  components: Record<ElectricityComponentKey, ScoreComponentDetails>;
  dominantComponent: ElectricityComponentKey | null;
}

export interface HeatFactorDetails {
  factor: number;
  currentYearConsumption: NullableNumber;
  lastYearConsumption: NullableNumber;
  yoyPct: NullableNumber;
  heatingSeasonKW: NullableNumber;
  outsideHeatingSeasonKW: NullableNumber;
  last30KW: NullableNumber;
  trend12ImpactKW: NullableNumber;
  trend30ImpactKW: NullableNumber;
  heatingSeasonScore: number;
  outsideHeatingSeasonScore: number;
  last30Score: number;
  trend12Score: number;
  trend30Score: number;
  components: Record<HeatComponentKey, ScoreComponentDetails>;
  dominantComponent: HeatComponentKey | null;
}

export interface WaterFactorDetails {
  factor: number;
  currentYearConsumption: NullableNumber;
  lastYearConsumption: NullableNumber;
  yoyPct: NullableNumber;
  outside30M3h: NullableNumber;
  trend12ImpactM3h: NullableNumber;
  trend30ImpactM3h: NullableNumber;
  outside30Score: number;
  trend12Score: number;
  trend30Score: number;
  components: Record<WaterComponentKey, ScoreComponentDetails>;
  dominantComponent: WaterComponentKey | null;
}

export interface DominantSignal {
  resource: ResourceName;
  component: ElectricityComponentKey | HeatComponentKey | WaterComponentKey;
  rawValue: number;
  contribution: number;
}

export interface PortfolioReMoniFactor {
  matrikelKey: string;
  address: string;
  remoniFactor: number;
  highestFactor: NullableNumber;
  averageFactor: NullableNumber;
  highestResource: ResourceName | null;
  displayLastYearConsumption: NullableNumber;
  displayTrendPct: NullableNumber;
  dominantSignal: DominantSignal | null;
  electricity?: ElectricityFactorDetails;
  heat?: HeatFactorDetails;
  water?: WaterFactorDetails;
}

interface ElectricityRawMetrics {
  averageKW: NullableNumber;
  previousAverageKW: NullableNumber;
  currentYearConsumption: NullableNumber;
  lastYearConsumption: NullableNumber;
  outsideKW: NullableNumber;
  outsideRatio: NullableNumber;
  trendImpactKW: NullableNumber;
  intensityKwhPerM2: NullableNumber;
  yoyPct: NullableNumber;
}

interface HeatRawMetrics {
  currentYearConsumption: NullableNumber;
  lastYearConsumption: NullableNumber;
  heatingSeasonKW: NullableNumber;
  outsideHeatingSeasonKW: NullableNumber;
  last30KW: NullableNumber;
  trend12ImpactKW: NullableNumber;
  trend30ImpactKW: NullableNumber;
  yoyPct: NullableNumber;
}

interface WaterRawMetrics {
  currentYearConsumption: NullableNumber;
  lastYearConsumption: NullableNumber;
  outside30M3h: NullableNumber;
  trend12ImpactM3h: NullableNumber;
  trend30ImpactM3h: NullableNumber;
  yoyPct: NullableNumber;
}

interface AggregatedMatrikelMetrics {
  matrikelKey: string;
  address: string;
  electricity: ElectricityRawMetrics | null;
  heat: HeatRawMetrics | null;
  water: WaterRawMetrics | null;
}

interface PortfolioCaps {
  electricity: {
    outsideKW: NullableNumber;
    trendImpactKW: NullableNumber;
    intensityKwhPerM2: NullableNumber;
  };
  heat: {
    heatingSeasonKW: NullableNumber;
    outsideHeatingSeasonKW: NullableNumber;
    last30KW: NullableNumber;
    trend12ImpactKW: NullableNumber;
    trend30ImpactKW: NullableNumber;
  };
  water: {
    outside30M3h: NullableNumber;
    trend12ImpactM3h: NullableNumber;
    trend30ImpactM3h: NullableNumber;
  };
}

interface MatrikelGroup {
  matrikelKey: string;
  address: string;
  powerMeters: PortfolioAnalysisResult[];
  heatMeters: PortfolioAnalysisResult[];
  waterMeters: PortfolioAnalysisResult[];
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function clampScore(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function applyMinimumCap(
  derivedCap: NullableNumber,
  minimumCap: NullableNumber,
): NullableNumber {
  if (isFiniteNumber(derivedCap) && isFiniteNumber(minimumCap)) {
    return Math.max(derivedCap, minimumCap);
  }

  if (isFiniteNumber(derivedCap)) {
    return derivedCap;
  }

  if (isFiniteNumber(minimumCap)) {
    return minimumCap;
  }

  return null;
}

export function percentile(
  values: Array<number | null | undefined>,
  percentileValue: number,
): NullableNumber {
  const sorted = values
    .filter(isFiniteNumber)
    .sort((left, right) => left - right);

  if (sorted.length === 0) return null;
  if (sorted.length === 1) return sorted[0];

  const position = (sorted.length - 1) * percentileValue;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);

  if (lowerIndex === upperIndex) return sorted[lowerIndex];

  const fraction = position - lowerIndex;
  return (
    sorted[lowerIndex] + (sorted[upperIndex] - sorted[lowerIndex]) * fraction
  );
}

export function cappedScore(
  value: number | null | undefined,
  cap: number | null | undefined,
): number {
  if (!isFiniteNumber(value) || !isFiniteNumber(cap) || cap <= 0) {
    return 0;
  }

  return clampScore((value / cap) * 100);
}

function sumFiniteValues(
  values: Array<number | null | undefined>,
): NullableNumber {
  const finiteValues = values.filter(isFiniteNumber);
  if (!finiteValues.length) {
    return null;
  }

  return finiteValues.reduce((sum, value) => sum + value, 0);
}

function calculateYoYPercentage(
  currentValue: NullableNumber,
  previousValue: NullableNumber,
): NullableNumber {
  if (
    !isFiniteNumber(currentValue) ||
    !isFiniteNumber(previousValue) ||
    previousValue === 0
  ) {
    return null;
  }

  return ((currentValue - previousValue) / previousValue) * 100;
}

function positiveDifference(
  currentValue: NullableNumber,
  previousValue: NullableNumber,
): NullableNumber {
  if (!isFiniteNumber(currentValue) || !isFiniteNumber(previousValue)) {
    return null;
  }

  return Math.max(0, currentValue - previousValue);
}

function buildScoreComponent(
  rawValue: NullableNumber,
  cap: NullableNumber,
  weight: number,
  scoreOverride?: number,
): ScoreComponentDetails {
  const available = rawValue !== null;
  const score = available
    ? clampScore(scoreOverride ?? cappedScore(rawValue, cap))
    : 0;

  return {
    rawValue,
    cap,
    score,
    weight,
    contribution: score * weight,
    available,
  };
}

function combineWeightedComponents(
  components: ScoreComponentDetails[],
): number {
  const availableComponents = components.filter(
    (component) => component.available,
  );
  const availableWeight = availableComponents.reduce(
    (sum, component) => sum + component.weight,
    0,
  );

  if (availableWeight <= 0) {
    return 0;
  }

  const weightedScore = availableComponents.reduce(
    (sum, component) => sum + component.contribution,
    0,
  );

  return weightedScore / availableWeight;
}

function getDominantComponent<Key extends string>(
  components: Record<Key, ScoreComponentDetails>,
): Key | null {
  const entries = (
    Object.entries(components) as Array<[Key, ScoreComponentDetails]>
  ).filter(([, component]) => component.available);

  if (!entries.length) {
    return null;
  }

  return entries.reduce((currentBest, nextEntry) =>
    nextEntry[1].contribution > currentBest[1].contribution
      ? nextEntry
      : currentBest,
  )[0];
}

function hasEnoughThirtyDayData(meter: PortfolioAnalysisResult): boolean {
  return (
    isFiniteNumber(meter.thirty_day_data_pct) &&
    meter.thirty_day_data_pct >= THIRTY_DAY_DATA_THRESHOLD
  );
}

function calculatePreviousThirtyDayAverage(
  meter: PortfolioAnalysisResult,
): NullableNumber {
  if (
    !isFiniteNumber(meter.last_30_days_average) ||
    !isFiniteNumber(meter.last_30_day_trend)
  ) {
    return null;
  }

  const denominator = 1 + meter.last_30_day_trend;
  if (!isFiniteNumber(denominator) || denominator <= 0) {
    return null;
  }

  return meter.last_30_days_average / denominator;
}

function aggregateThirtyDayTrendImpact(
  meters: PortfolioAnalysisResult[],
): NullableNumber {
  const comparableMeters = meters
    .filter(hasEnoughThirtyDayData)
    .map((meter) => ({
      currentValue: meter.last_30_days_average,
      previousValue: calculatePreviousThirtyDayAverage(meter),
    }))
    .filter(
      (entry): entry is { currentValue: number; previousValue: number } =>
        isFiniteNumber(entry.currentValue) &&
        isFiniteNumber(entry.previousValue),
    );

  const currentTotal = sumFiniteValues(
    comparableMeters.map((entry) => entry.currentValue),
  );
  const previousTotal = sumFiniteValues(
    comparableMeters.map((entry) => entry.previousValue),
  );

  return positiveDifference(currentTotal, previousTotal);
}

function buildMatrikelGroups(
  powerList: PortfolioAnalysisResult[] | undefined,
  heatList: PortfolioAnalysisResult[] | undefined,
  waterList: PortfolioAnalysisResult[] | undefined,
): MatrikelGroup[] {
  const groups = new Map<string, MatrikelGroup>();

  const addMeters = (
    meters: PortfolioAnalysisResult[] | undefined,
    resource: 'power' | 'heat' | 'water',
  ) => {
    if (!meters) {
      return;
    }

    for (const meter of meters) {
      if (meter.type_of_mp === 'PV') {
        continue;
      }

      const matrikelKey = meter.matrikel_key || meter.address;
      if (!matrikelKey) {
        continue;
      }

      const existingGroup = groups.get(matrikelKey);
      const group = existingGroup ?? {
        matrikelKey,
        address: meter.main_address ?? meter.address ?? matrikelKey,
        powerMeters: [],
        heatMeters: [],
        waterMeters: [],
      };

      if (resource === 'power') {
        group.powerMeters.push(meter);
      } else if (resource === 'heat') {
        group.heatMeters.push(meter);
      } else {
        group.waterMeters.push(meter);
      }

      groups.set(matrikelKey, group);
    }
  };

  addMeters(powerList, 'power');
  addMeters(heatList, 'heat');
  addMeters(waterList, 'water');

  return Array.from(groups.values());
}

function aggregateElectricityMetrics(
  meters: PortfolioAnalysisResult[],
): ElectricityRawMetrics | null {
  if (!meters.length) {
    return null;
  }

  const averageKW = sumFiniteValues(meters.map((meter) => meter.average_all));
  const previousAverageKW = sumFiniteValues(
    meters.map((meter) => meter.average_all_last_year),
  );
  const outsideKW = sumFiniteValues(
    meters.map((meter) => meter.average_all_inactive_hours),
  );
  const currentYearConsumption = sumFiniteValues(
    meters.map((meter) => meter.total_consumption_year),
  );
  const lastYearConsumption = sumFiniteValues(
    meters.map((meter) => meter.total_consumption_last_year),
  );

  let outsideRatio: NullableNumber = null;
  if (isFiniteNumber(averageKW) && isFiniteNumber(outsideKW)) {
    outsideRatio = averageKW > 0 ? Math.min(1, outsideKW / averageKW) : 0;
  }

  return {
    averageKW,
    previousAverageKW,
    currentYearConsumption,
    lastYearConsumption,
    outsideKW,
    outsideRatio,
    trendImpactKW: positiveDifference(averageKW, previousAverageKW),
    intensityKwhPerM2: null,
    yoyPct: calculateYoYPercentage(currentYearConsumption, lastYearConsumption),
  };
}

function aggregateHeatMetrics(
  meters: PortfolioAnalysisResult[],
): HeatRawMetrics | null {
  if (!meters.length) {
    return null;
  }

  const currentYearConsumption = sumFiniteValues(
    meters.map((meter) => meter.total_consumption_year),
  );
  const lastYearConsumption = sumFiniteValues(
    meters.map((meter) => meter.total_consumption_last_year),
  );

  const annualIncrease = positiveDifference(
    currentYearConsumption,
    lastYearConsumption,
  );

  return {
    currentYearConsumption,
    lastYearConsumption,
    heatingSeasonKW: sumFiniteValues(
      meters.map((meter) => meter.average_heating_season),
    ),
    outsideHeatingSeasonKW: sumFiniteValues(
      meters.map((meter) => meter.average_non_heating_season),
    ),
    last30KW: sumFiniteValues(
      meters
        .filter(hasEnoughThirtyDayData)
        .map((meter) => meter.last_30_days_average),
    ),
    trend12ImpactKW:
      annualIncrease === null
        ? null
        : Math.max(0, annualIncrease / HOURS_PER_YEAR),
    trend30ImpactKW: aggregateThirtyDayTrendImpact(meters),
    yoyPct: calculateYoYPercentage(currentYearConsumption, lastYearConsumption),
  };
}

function aggregateWaterMetrics(
  meters: PortfolioAnalysisResult[],
): WaterRawMetrics | null {
  if (!meters.length) {
    return null;
  }

  const currentAverageM3h = sumFiniteValues(
    meters.map((meter) => meter.average_all),
  );
  const previousAverageM3h = sumFiniteValues(
    meters.map((meter) => meter.average_all_last_year),
  );

  const currentYearConsumption = sumFiniteValues(
    meters.map((meter) => meter.total_consumption_year),
  );
  const lastYearConsumption = sumFiniteValues(
    meters.map((meter) => meter.total_consumption_last_year),
  );

  return {
    currentYearConsumption,
    lastYearConsumption,
    outside30M3h: sumFiniteValues(
      meters
        .filter(hasEnoughThirtyDayData)
        .map((meter) => meter.last_30_days_inactive_period),
    ),
    trend12ImpactM3h: positiveDifference(currentAverageM3h, previousAverageM3h),
    trend30ImpactM3h: aggregateThirtyDayTrendImpact(meters),
    yoyPct: calculateYoYPercentage(currentYearConsumption, lastYearConsumption),
  };
}

function aggregatePortfolioMetrics(
  powerList: PortfolioAnalysisResult[] | undefined,
  heatList: PortfolioAnalysisResult[] | undefined,
  waterList: PortfolioAnalysisResult[] | undefined,
): AggregatedMatrikelMetrics[] {
  return buildMatrikelGroups(powerList, heatList, waterList).map((group) => ({
    matrikelKey: group.matrikelKey,
    address: group.address,
    electricity: aggregateElectricityMetrics(group.powerMeters),
    heat: aggregateHeatMetrics(group.heatMeters),
    water: aggregateWaterMetrics(group.waterMeters),
  }));
}

function calculatePortfolioCaps(
  portfolio: AggregatedMatrikelMetrics[],
): PortfolioCaps {
  return {
    electricity: {
      outsideKW: applyMinimumCap(
        percentile(
          portfolio.map((matrikel) => matrikel.electricity?.outsideKW),
          0.995,
        ),
        MINIMUM_SCORE_CAPS.electricity.outsideKW,
      ),
      trendImpactKW: applyMinimumCap(
        percentile(
          portfolio.map((matrikel) => matrikel.electricity?.trendImpactKW),
          0.99,
        ),
        MINIMUM_SCORE_CAPS.electricity.trendImpactKW,
      ),
      intensityKwhPerM2: percentile(
        portfolio.map((matrikel) => matrikel.electricity?.intensityKwhPerM2),
        0.95,
      ),
    },
    heat: {
      heatingSeasonKW: percentile(
        portfolio.map((matrikel) => matrikel.heat?.heatingSeasonKW),
        0.95,
      ),
      outsideHeatingSeasonKW: percentile(
        portfolio.map((matrikel) => matrikel.heat?.outsideHeatingSeasonKW),
        0.95,
      ),
      last30KW: percentile(
        portfolio.map((matrikel) => matrikel.heat?.last30KW),
        0.95,
      ),
      trend12ImpactKW: percentile(
        portfolio.map((matrikel) => matrikel.heat?.trend12ImpactKW),
        0.95,
      ),
      trend30ImpactKW: percentile(
        portfolio.map((matrikel) => matrikel.heat?.trend30ImpactKW),
        0.95,
      ),
    },
    water: {
      outside30M3h: percentile(
        portfolio.map((matrikel) => matrikel.water?.outside30M3h),
        0.99,
      ),
      trend12ImpactM3h: percentile(
        portfolio.map((matrikel) => matrikel.water?.trend12ImpactM3h),
        0.99,
      ),
      trend30ImpactM3h: percentile(
        portfolio.map((matrikel) => matrikel.water?.trend30ImpactM3h),
        0.99,
      ),
    },
  };
}

function buildElectricityFactor(
  rawMetrics: ElectricityRawMetrics | null,
  caps: PortfolioCaps['electricity'],
): ElectricityFactorDetails | undefined {
  if (!rawMetrics) {
    return undefined;
  }

  const components: Record<ElectricityComponentKey, ScoreComponentDetails> = {
    outsideKW: buildScoreComponent(rawMetrics.outsideKW, caps.outsideKW, 0.7),
    outsideRatio: buildScoreComponent(
      rawMetrics.outsideRatio,
      0.8,
      0.1,
      rawMetrics.outsideRatio === null
        ? undefined
        : cappedScore(rawMetrics.outsideRatio, 0.8),
    ),
    trend: buildScoreComponent(
      rawMetrics.trendImpactKW,
      caps.trendImpactKW,
      0.1,
    ),
    intensity: buildScoreComponent(
      rawMetrics.intensityKwhPerM2,
      caps.intensityKwhPerM2,
      0.1,
    ),
  };

  if (!Object.values(components).some((component) => component.available)) {
    return undefined;
  }

  return {
    factor: combineWeightedComponents(Object.values(components)),
    averageKW: rawMetrics.averageKW,
    previousAverageKW: rawMetrics.previousAverageKW,
    currentYearConsumption: rawMetrics.currentYearConsumption,
    lastYearConsumption: rawMetrics.lastYearConsumption,
    yoyPct: rawMetrics.yoyPct,
    outsideKW: rawMetrics.outsideKW,
    outsideRatio: rawMetrics.outsideRatio,
    trendImpactKW: rawMetrics.trendImpactKW,
    intensityKwhPerM2: rawMetrics.intensityKwhPerM2,
    outsideKWScore: components.outsideKW.score,
    outsideRatioScore: components.outsideRatio.score,
    trendScore: components.trend.score,
    intensityScore: components.intensity.available
      ? components.intensity.score
      : null,
    components,
    dominantComponent: getDominantComponent(components),
  };
}

function buildHeatFactor(
  rawMetrics: HeatRawMetrics | null,
  caps: PortfolioCaps['heat'],
): HeatFactorDetails | undefined {
  if (!rawMetrics) {
    return undefined;
  }

  const components: Record<HeatComponentKey, ScoreComponentDetails> = {
    heatingSeason: buildScoreComponent(
      rawMetrics.heatingSeasonKW,
      caps.heatingSeasonKW,
      0.2,
    ),
    outsideHeatingSeason: buildScoreComponent(
      rawMetrics.outsideHeatingSeasonKW,
      caps.outsideHeatingSeasonKW,
      0.4,
    ),
    last30: buildScoreComponent(rawMetrics.last30KW, caps.last30KW, 0.15),
    trend12: buildScoreComponent(
      rawMetrics.trend12ImpactKW,
      caps.trend12ImpactKW,
      0.15,
    ),
    trend30: buildScoreComponent(
      rawMetrics.trend30ImpactKW,
      caps.trend30ImpactKW,
      0.1,
    ),
  };

  if (!Object.values(components).some((component) => component.available)) {
    return undefined;
  }

  return {
    factor: combineWeightedComponents(Object.values(components)),
    currentYearConsumption: rawMetrics.currentYearConsumption,
    lastYearConsumption: rawMetrics.lastYearConsumption,
    yoyPct: rawMetrics.yoyPct,
    heatingSeasonKW: rawMetrics.heatingSeasonKW,
    outsideHeatingSeasonKW: rawMetrics.outsideHeatingSeasonKW,
    last30KW: rawMetrics.last30KW,
    trend12ImpactKW: rawMetrics.trend12ImpactKW,
    trend30ImpactKW: rawMetrics.trend30ImpactKW,
    heatingSeasonScore: components.heatingSeason.score,
    outsideHeatingSeasonScore: components.outsideHeatingSeason.score,
    last30Score: components.last30.score,
    trend12Score: components.trend12.score,
    trend30Score: components.trend30.score,
    components,
    dominantComponent: getDominantComponent(components),
  };
}

function buildWaterFactor(
  rawMetrics: WaterRawMetrics | null,
  caps: PortfolioCaps['water'],
): WaterFactorDetails | undefined {
  if (!rawMetrics) {
    return undefined;
  }

  const components: Record<WaterComponentKey, ScoreComponentDetails> = {
    outside30: buildScoreComponent(
      rawMetrics.outside30M3h,
      caps.outside30M3h,
      0.8,
    ),
    trend12: buildScoreComponent(
      rawMetrics.trend12ImpactM3h,
      caps.trend12ImpactM3h,
      0.1,
    ),
    trend30: buildScoreComponent(
      rawMetrics.trend30ImpactM3h,
      caps.trend30ImpactM3h,
      0.1,
    ),
  };

  if (!Object.values(components).some((component) => component.available)) {
    return undefined;
  }

  return {
    factor: combineWeightedComponents(Object.values(components)),
    currentYearConsumption: rawMetrics.currentYearConsumption,
    lastYearConsumption: rawMetrics.lastYearConsumption,
    yoyPct: rawMetrics.yoyPct,
    outside30M3h: rawMetrics.outside30M3h,
    trend12ImpactM3h: rawMetrics.trend12ImpactM3h,
    trend30ImpactM3h: rawMetrics.trend30ImpactM3h,
    outside30Score: components.outside30.score,
    trend12Score: components.trend12.score,
    trend30Score: components.trend30.score,
    components,
    dominantComponent: getDominantComponent(components),
  };
}

function buildDominantSignal(
  electricity: ElectricityFactorDetails | undefined,
  heat: HeatFactorDetails | undefined,
  water: WaterFactorDetails | undefined,
): DominantSignal | null {
  const candidates: DominantSignal[] = [];

  if (electricity?.dominantComponent) {
    const component = electricity.components[electricity.dominantComponent];
    if (component.available && isFiniteNumber(component.rawValue)) {
      candidates.push({
        resource: 'electricity',
        component: electricity.dominantComponent,
        rawValue: component.rawValue,
        contribution: component.contribution,
      });
    }
  }

  if (heat?.dominantComponent) {
    const component = heat.components[heat.dominantComponent];
    if (component.available && isFiniteNumber(component.rawValue)) {
      candidates.push({
        resource: 'heat',
        component: heat.dominantComponent,
        rawValue: component.rawValue,
        contribution: component.contribution,
      });
    }
  }

  if (water?.dominantComponent) {
    const component = water.components[water.dominantComponent];
    if (component.available && isFiniteNumber(component.rawValue)) {
      candidates.push({
        resource: 'water',
        component: water.dominantComponent,
        rawValue: component.rawValue,
        contribution: component.contribution,
      });
    }
  }

  if (!candidates.length) {
    return null;
  }

  return candidates.reduce((currentBest, nextSignal) =>
    nextSignal.contribution > currentBest.contribution
      ? nextSignal
      : currentBest,
  );
}

function getHighestResourceFactor(
  electricity: ElectricityFactorDetails | undefined,
  heat: HeatFactorDetails | undefined,
  water: WaterFactorDetails | undefined,
): {
  highestResource: ResourceName | null;
  highestFactor: NullableNumber;
  averageFactor: NullableNumber;
  remoniFactor: number;
} {
  const availableFactors = [
    electricity
      ? { resource: 'electricity' as const, factor: electricity.factor }
      : null,
    heat ? { resource: 'heat' as const, factor: heat.factor } : null,
    water ? { resource: 'water' as const, factor: water.factor } : null,
  ].filter(
    (entry): entry is { resource: ResourceName; factor: number } =>
      entry !== null && isFiniteNumber(entry.factor),
  );

  if (!availableFactors.length) {
    return {
      highestResource: null,
      highestFactor: null,
      averageFactor: null,
      remoniFactor: 0,
    };
  }

  const highestEntry = availableFactors.reduce((currentBest, nextEntry) =>
    nextEntry.factor > currentBest.factor ? nextEntry : currentBest,
  );
  const averageFactor =
    availableFactors.reduce((sum, entry) => sum + entry.factor, 0) /
    availableFactors.length;

  return {
    highestResource: highestEntry.resource,
    highestFactor: highestEntry.factor,
    averageFactor,
    remoniFactor: highestEntry.factor * 0.6 + averageFactor * 0.4,
  };
}

function getDisplayMetrics(
  highestResource: ResourceName | null,
  electricity: ElectricityFactorDetails | undefined,
  heat: HeatFactorDetails | undefined,
  water: WaterFactorDetails | undefined,
): {
  displayLastYearConsumption: NullableNumber;
  displayTrendPct: NullableNumber;
} {
  if (highestResource === 'electricity' && electricity) {
    return {
      displayLastYearConsumption: electricity.lastYearConsumption,
      displayTrendPct: electricity.yoyPct,
    };
  }

  if (highestResource === 'heat' && heat) {
    return {
      displayLastYearConsumption: heat.lastYearConsumption,
      displayTrendPct: heat.yoyPct,
    };
  }

  if (highestResource === 'water' && water) {
    return {
      displayLastYearConsumption: water.lastYearConsumption,
      displayTrendPct: water.yoyPct,
    };
  }

  return {
    displayLastYearConsumption: null,
    displayTrendPct: null,
  };
}

export function calculatePortfolioReMoniFactors(
  powerList: PortfolioAnalysisResult[] | undefined,
  heatList: PortfolioAnalysisResult[] | undefined,
  waterList: PortfolioAnalysisResult[] | undefined,
): PortfolioReMoniFactor[] {
  const aggregatedPortfolio = aggregatePortfolioMetrics(
    powerList,
    heatList,
    waterList,
  );
  const caps = calculatePortfolioCaps(aggregatedPortfolio);

  return aggregatedPortfolio
    .map((matrikel) => {
      const electricity = buildElectricityFactor(
        matrikel.electricity,
        caps.electricity,
      );
      const heat = buildHeatFactor(matrikel.heat, caps.heat);
      const water = buildWaterFactor(matrikel.water, caps.water);

      const combinedFactor = getHighestResourceFactor(electricity, heat, water);
      const displayMetrics = getDisplayMetrics(
        combinedFactor.highestResource,
        electricity,
        heat,
        water,
      );

      return {
        matrikelKey: matrikel.matrikelKey,
        address: matrikel.address,
        remoniFactor: combinedFactor.remoniFactor,
        highestFactor: combinedFactor.highestFactor,
        averageFactor: combinedFactor.averageFactor,
        highestResource: combinedFactor.highestResource,
        displayLastYearConsumption: displayMetrics.displayLastYearConsumption,
        displayTrendPct: displayMetrics.displayTrendPct,
        dominantSignal: buildDominantSignal(electricity, heat, water),
        electricity,
        heat,
        water,
      };
    })
    .filter(
      (matrikel) => matrikel.electricity || matrikel.heat || matrikel.water,
    )
    .sort((left, right) => right.remoniFactor - left.remoniFactor);
}
